<template>
    <div class="assign-container">
        <span>License</span>
        <div class="section-wrapper">
            <div class="section-container">
                <span>Select</span>
                <div v-if="isLoadingContacts" class="loading-container">
                    <div class="loading-spinner"></div>
                    <span class="loading-text">Loading contacts...</span>
                </div>
                <PrimeSelect v-else class="w-full" v-model="contactSelect" :options="contacts" optionLabel="name" optionValue="id" placeholder="Unassigned" />
            </div>
        </div>
    </div>
    <div class="assign-footer">
        <div class="footer-actions">
            <prime-button @click="close" variant="secondary" label="Cancel" class="p-btn-root_secondary" />
            <prime-button label="Assign" variant="primary" @click="assignLicense" :loading="isAssigning" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';
import { onMounted, ref } from 'vue';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { usePersonStore } from '~/stores/person';
import { useContactsStore } from '~/stores/person/contacts';
import { useLicenseStore } from '~/stores/person/licenses';
import { ModalNames } from '~/types/modals';
import { ContactType, type ContactSelect } from '~/types/person/contacts';

const contactsStore = useContactsStore();
const personStore = usePersonStore();
const authStore = useAuthStore();
const dialogRef = getDialogRef(ModalNames.ASSIGN_LICENSE);

const contactSelect = ref(personStore.selectedPersonForLicense?.assigned_to?.id || null);
const contactType = ref<ContactType>();
const isLoadingContacts = ref(true);
const isAssigning = ref(false);
const { showSuccess, showError, showWarning } = useGlobalToast();

const contacts = ref<ContactSelect[]>([]);

// Fetch contacts with loading state
const fetchContacts = async () => {
    try {
        isLoadingContacts.value = true;
        await contactsStore.fetchContacts();

        // Populate contacts after fetching
        if (!personStore.person?.pro) {
            contacts.value = [];
            contacts.value.push({
                id: personStore.person?.id!,
                type: ContactType.PERSON,
                name: personStore.person?.name!
            });
        }


        contactsStore.contacts.forEach(contact => {
            if ( contact.licensed) return;
            
            if (contact.trusted_person_id) {
                contacts.value.push({ name: contact.name, id: contact.trusted_person_id, type: ContactType.PERSON });
            } else {
                contacts.value.push({ name: contact.name, id: -contact.id, type: ContactType.CONTACT });
            }
        });
    } catch (error) {
        showError('Error', 'Failed to load contacts');
    } finally {
        isLoadingContacts.value = false;
    }
};

// Call fetchContacts on component mount
onMounted(() => {
    fetchContacts();
});

const close = () => {
    dialogRef.value?.close();
};

const assignLicense = async () => {
    try {
        isAssigning.value = true;
        
        if (contactSelect.value) {
            contacts.value.some(contact => {
                if (contact.id === contactSelect.value) {
                    contactType.value = contact.type!;
                }
            });
        }
        
        const licenseStore = useLicenseStore();
        
        const success = await licenseStore.assignLicense(
            authStore.user?.identity?.id!,
            dialogRef.value?.data?.license?.id!,
            contactSelect.value!,
            contactType.value!
        );

        if (success) {
            showSuccess('Success', 'License assigned successfully');
            close();
        } else {
            showError('Error', licenseStore.error || 'Failed to assign license');
        }
    } catch (err) {
        showError('Error', 'Unexpected error occurred');
    } finally {
        isAssigning.value = false;
    }
};
</script>

<style lang="scss" scoped>
.section-wrapper {
    @apply flex flex-col items-center gap-4 self-stretch p-6 px-4 rounded-lg border border-black/10 bg-white;
    // @apply shadow-[0px_1px_3px_rgba(10, 13, 18, 0.10), 0px_1px_2px_-1px_rgba(10, 13, 18, 0.10)];

    .section-container {
        @apply flex flex-col items-start gap-1.5 self-stretch;

        span {
            @apply text-gray-700 text-sm font-medium leading-5;
        }

        .loading-container {
            @apply flex flex-col items-center justify-center gap-3 w-full py-2;

            .loading-spinner {
                @apply w-6 h-6 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin;
            }

            .loading-text {
                @apply text-gray-500 text-sm font-medium;
            }
        }
    }
}

.assign-container {
    @apply flex flex-col items-start gap-3 px-6 self-stretch overflow-y-auto;

    span {
        @apply text-zinc-900 font-semibold text-sm leading-6 font-inter;
    }
}

.assign-footer {
    @apply flex flex-col justify-center items-end self-stretch pt-8;

    .footer-actions {
        @apply flex justify-end items-center gap-3 flex-[1_0_0] px-6 pb-6;
    }
}
</style>
